import { AxiosError } from "axios";
import { PostHog } from "posthog-js";
import type { IReferral, ReferralUpdateParams } from "@/referrals/referral";

interface ReferralEventBaseProperties {
  referral_id: string;
  old_status?: string;
  new_status?: string;
  error?: string;
}

interface ReferralUpdateEventProperties extends ReferralEventBaseProperties {
  updated_fields: string[];
  has_appointment: boolean;
  has_deal_size: boolean;
  method?: "drag_and_drop" | "sidebar" | "modal";
}

export const trackReferralUpdate = (
  posthog: PostHog | null,
  {
    referral,
    updates,
    method,
    success,
    error,
  }: {
    referral: IReferral;
    updates: Partial<ReferralUpdateParams>;
    method?: "drag_and_drop" | "sidebar" | "modal";
    success: boolean;
    error?: Error | AxiosError;
  },
) => {
  if (!posthog) return;

  const baseProperties: ReferralUpdateEventProperties = {
    referral_id: referral.id,
    old_status: referral.status,
    new_status: updates.status,
    updated_fields: Object.keys(updates),
    has_appointment: !!updates.appointment_date,
    has_deal_size: !!updates.deal_size,
    method,
  };

  if (success) {
    posthog.capture("referral_updated", baseProperties);
  } else {
    posthog.capture("referral_update_failed", {
      ...baseProperties,
      error:
        error instanceof AxiosError
          ? error.response?.data?.error
          : "Unknown error",
    });
  }
};

export const trackReferralCreation = (
  posthog: PostHog | null,
  {
    referral,
    success,
    error,
    data,
  }: {
    referral?: IReferral;
    success: boolean;
    error?: Error | AxiosError;
    data?: Record<string, unknown>;
  },
) => {
  if (!posthog) return;

  if (success && referral) {
    posthog.capture("referral_created", {
      referral_id: referral.id,
      service_needed: referral.service_needed,
      has_customer_name: !!referral.potential_customer.name,
      advocate_id: referral.advocate.id,
    });
  } else {
    posthog.capture("referral_creation_failed", {
      attempted_data: data,
      error:
        error instanceof AxiosError
          ? error.response?.data?.error
          : "Unknown error",
    });
  }
};
